// The AI screen. Admins: mode, model, the company's Claude API key, the knowledge the AI reads, the availability table
// (+ CSV / Google Sheet link), files, hand-over rules, the log of what each answer used. Admins and agents: "Try the AI"
// (a chat with it, no WhatsApp).
import { callFn, rpc, sb } from "../sb.ts";
import { t } from "../i18n.ts";
import { fmtTime, h, mount, toast } from "../ui.ts";
import { unzipSync, strFromU8 } from "fflate";

interface Faq { q: string; a: string }
interface Row { id: string; item: string; category: string; status: string; price: string; details: string }
const MODELS = [
  { id: "claude-opus-5-5", label: "Claude Opus 5.5 — best answers (default)" },
  { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5 — fast, lower cost" },
  { id: "claude-haiku-5-5", label: "Claude Haiku 5.5 — fastest, lowest cost" },
];
const STATUSES = ["available", "reserved", "sold", "unavailable"];
const TEXTS: [string, string, string][] = [
  ["about", "About us", "Who you are, what you sell or rent, where you are, how to reach you."],
  ["prices", "Prices", "Price lists, fees, what is included. The AI quotes only what is written here or in the availability."],
  ["hours", "Opening hours", "e.g. Sat–Thu 9:00–17:00, Friday closed."],
  ["policies", "Policies", "Booking, deposits, cancellations, viewing, delivery …"],
  ["tone", "Tone of voice", "e.g. Warm and short. Greet with 'Hello' / 'السلام عليكم'."],
  ["never_say", "Never say or promise", "e.g. No discounts. Never confirm a booking. Never give the owner's number."],
  ["extra", "More", "Anything else the AI should know."],
];

export async function aiPage(view: HTMLElement, role: string) {
  const admin = role === "admin";
  const box = h("section", { class: "page" });
  mount(view, box);
  const { data: s } = await sb.from("ai_settings").select("*").maybeSingle();
  if (!s) { mount(box, h("p", {}, t("The AI tables are not in this project yet (migration 005).")) ); return; }
  if (!admin) { mount(box, h("h2", {}, t("Try the AI")), tryBox()); return; }

  const keySet = await rpc<boolean>("provider_key_set", { p_name: "ai_api_key" }).catch(() => false);
  const { data: lines } = await sb.from("wa_lines").select("line").order("created_at").limit(1);
  const staff = lines?.[0] ? await rpc<{ email: string; name: string }[]>("chat_staff", { p_line: lines[0].line }).catch(() => []) : [];

  // ---- mode + model + rules ----
  const mode = h("select", {}, (["off", "suggest", "auto"] as const).map((m) => h("option", { value: m, selected: s.mode === m },
    { off: t("Off"), suggest: t("Suggest: a draft in the chat, staff send it"), auto: t("Auto: answers by itself (24-hour window, limits, hand-over)") }[m])));
  const model = h("select", {}, MODELS.map((m) => h("option", { value: m.id, selected: s.model === m.id }, m.label)),
    !MODELS.some((m) => m.id === s.model) && h("option", { value: s.model, selected: true }, s.model));
  const name = h("input", { value: s.agent_name });
  const lang = h("select", {}, [["auto", t("Same as the customer (Arabic / English)")], ["ar", "العربية"], ["en", "English"]]
    .map(([v, l]) => h("option", { value: v, selected: s.reply_language === v }, l)));
  const perHour = h("input", { type: "number", min: 1, max: 30, value: s.max_per_hour });
  const handTo = h("select", {}, h("option", { value: "" }, t("Nobody (stays unassigned; everyone is notified)")),
    staff.map((p) => h("option", { value: p.email, selected: s.handover_to === p.email }, p.name)));
  const handMsg = h("textarea", { rows: 2, dir: "auto", placeholder: t("e.g. Thank you, a colleague will reply shortly. / شكراً، سيتواصل معك أحد الزملاء قريباً.") }, s.handover_message);
  const topics = h("input", { value: s.handover_topics });

  // ---- knowledge ----
  const texts = new Map<string, HTMLTextAreaElement>();
  for (const [k] of TEXTS) texts.set(k, h("textarea", { rows: k === "about" || k === "prices" || k === "policies" ? 5 : 3, dir: "auto" }, s[k] ?? ""));
  let faqs: Faq[] = (s.faqs ?? []).map((f: Faq) => ({ ...f }));
  const faqBox = h("div", { class: "faqs" });
  function renderFaqs() {
    mount(faqBox, faqs.map((f, i) => h("div", { class: "faq" },
      h("input", { value: f.q, dir: "auto", placeholder: t("Question"), oninput: (e: Event) => { f.q = (e.target as HTMLInputElement).value; } }),
      h("textarea", { rows: 2, dir: "auto", placeholder: t("Answer"), oninput: (e: Event) => { f.a = (e.target as HTMLTextAreaElement).value; } }, f.a),
      h("button", { class: "link", title: t("Remove"), onclick: () => { faqs.splice(i, 1); renderFaqs(); } }, "✕"))),
      h("button", { onclick: () => { faqs.push({ q: "", a: "" }); renderFaqs(); } }, "+ " + t("Add a question")));
  }
  renderFaqs();

  const save = async () => {
    const p: Record<string, unknown> = { mode: mode.value, model: model.value, agent_name: name.value, reply_language: lang.value,
      max_per_hour: Number(perHour.value) || 4, handover_to: handTo.value, handover_message: handMsg.value, handover_topics: topics.value, faqs,
      sheet_url: sheet.value.trim() };
    for (const [k, el] of texts) p[k] = el.value;
    try { await rpc("save_ai_settings", { p }); toast(t("Saved")); } catch (e) { toast((e as Error).message, true); }
  };

  // ---- key ----
  const key = h("input", { type: "password", autocomplete: "new-password", placeholder: keySet ? t("saved — paste only to replace") : "sk-ant-…", dir: "ltr" });
  const keyOut = h("span", { class: "muted" });
  const saveKey = async () => {
    try {
      if (key.value.trim()) { await rpc("save_provider_secret", { p_name: "ai_api_key", p_value: key.value.trim() }); key.value = ""; key.placeholder = t("saved — paste only to replace"); }
      keyOut.textContent = t("Checking…");
      const r = await callFn("ai-agent", { mode: "check" });
      keyOut.textContent = r.ok ? `✓ ${r.name ?? r.model}` : "✗ " + (r.error ?? "");
    } catch (e) { keyOut.textContent = "✗ " + (e as Error).message; }
  };

  // ---- availability ----
  let rows: Row[] = (s.availability ?? []).map((r: Row) => ({ ...r }));
  const table = h("div", { class: "avail" });
  function renderRows() {
    const cell = (r: Row, k: keyof Row, ph: string) => h("input", { value: r[k], dir: "auto", placeholder: ph, oninput: (e: Event) => { r[k] = (e.target as HTMLInputElement).value; } });
    mount(table, h("table", {}, h("thead", {}, h("tr", {}, [t("Item"), t("Category"), t("Status"), t("Price"), t("Details"), ""].map((x) => h("th", {}, x)))),
      h("tbody", {}, rows.map((r, i) => h("tr", {},
        h("td", {}, cell(r, "item", t("e.g. Flat 101"))), h("td", {}, cell(r, "category", t("e.g. 2 bedrooms"))),
        h("td", {}, h("select", { onchange: (e: Event) => { r.status = (e.target as HTMLSelectElement).value; } },
          STATUSES.map((x) => h("option", { value: x, selected: r.status === x }, t(x))))),
        h("td", {}, cell(r, "price", "300 OMR")), h("td", {}, cell(r, "details", "")),
        h("td", {}, h("button", { class: "link", title: t("Remove"), onclick: () => { rows.splice(i, 1); renderRows(); } }, "✕")))))),
      h("div", { class: "row end" }, h("button", { onclick: () => { rows.push({ id: "r" + Date.now().toString(36) + rows.length, item: "", category: "", status: "available", price: "", details: "" }); renderRows(); } }, "+ " + t("Add a row")),
        h("button", { class: "primary", onclick: async () => {
          try { const n = await rpc<number>("save_ai_availability", { p_rows: rows }); toast(t("Saved: {n} rows", { n })); } catch (e) { toast((e as Error).message, true); }
        } }, t("Save the table"))));
  }
  renderRows();
  const sheet = h("input", { value: s.sheet_url ?? "", placeholder: "https://docs.google.com/spreadsheets/d/…", dir: "ltr" });
  const sheetOut = h("p", { class: "muted" });

  // ---- files ----
  const filesBox = h("div", {});
  async function renderFiles() {
    const { data: fs, error } = await sb.storage.from("ai-docs").list("", { limit: 100, sortBy: { column: "name", order: "asc" } });
    if (error) return mount(filesBox, h("p", { class: "err" }, error.message));
    const real = (fs ?? []).filter((f) => f.id && f.name !== ".emptyFolderPlaceholder");
    const up = h("input", { type: "file", accept: ".pdf,.txt,.md,.csv,.zip,application/pdf,text/plain,text/markdown,text/csv,application/zip" });
    up.addEventListener("change", async () => {
      const f = up.files?.[0];
      up.value = "";
      if (!f) return;
      try {
        const files = /\.zip$/i.test(f.name) ? await fromZip(f) : [{ name: f.name, blob: f as Blob }];
        for (const x of files) {
          if (x.blob.size > 4 * 1024 * 1024) { toast(t("4 MB at most per file") + ": " + x.name, true); continue; }
          const nm = x.name.replace(/[^\p{L}\p{N}._ -]/gu, "_").slice(-120);
          const type = /\.pdf$/i.test(nm) ? "application/pdf" : /\.md$/i.test(nm) ? "text/markdown" : /\.csv$/i.test(nm) ? "text/csv" : "text/plain";
          const { error: e } = await sb.storage.from("ai-docs").upload(nm, x.blob, { contentType: type, upsert: false });
          if (e) toast(nm + ": " + (/exists|duplicate/i.test(e.message) ? t("a file with this name is already there: remove it first") : e.message), true);
          else toast(t("Uploaded") + ": " + nm);
        }
      } catch (e) { toast((e as Error).message, true); }
      renderFiles();
    });
    mount(filesBox, h("ul", { class: "files" }, real.length ? real.map((f) => h("li", {}, "📄 ", h("span", { dir: "auto" }, f.name),
      h("small", { class: "muted" }, ` ${Math.ceil(Number(f.metadata?.size ?? 0) / 1024)} KB`),
      h("button", { class: "link", title: t("Remove"), onclick: async () => {
        if (!confirm(t("Remove {name}?", { name: f.name }))) return;
        const r = await callFn("ai-agent", { mode: "file_delete", name: f.name });
        if (!r.ok) return toast(r.error ?? "", true);
        renderFiles();
      } }, "✕"))) : h("li", { class: "muted" }, t("No files yet"))),
      h("label", {}, t("Add a file (PDF, text, or a skill .zip; 4 MB at most; up to 10 files are read)"), up));
  }
  renderFiles();

  // ---- Agent Skills (a skill folder zipped: SKILL.md + references + data files, up to 30 MB) ----
  const skillsBox = h("div", {});
  let skills: { id: string; name: string; version?: string; size?: number; uploaded_at?: string }[] = s.skills ?? [];
  function renderSkills() {
    const pick = (skillId?: string) => {
      const inp = h("input", { type: "file", accept: ".zip,application/zip" });
      inp.addEventListener("change", async () => {
        const f = inp.files?.[0];
        if (!f) return;
        if (f.size > 30 * 1024 * 1024) return toast(t("30 MB at most"), true);
        const out = h("p", { class: "muted" }, t("Uploading…"));
        skillsBox.appendChild(out);
        const path = crypto.randomUUID() + ".zip";
        const { error } = await sb.storage.from("ai-skills").upload(path, f, { contentType: "application/zip", upsert: false });
        if (error) { out.textContent = "✗ " + error.message; return; }
        out.textContent = t("Sending to your Claude account… (up to a minute)");
        const r = await callFn("ai-agent", { mode: "skill_upload", path, skill_id: skillId });
        if (!r.ok) { out.textContent = "✗ " + (r.error ?? ""); return; }
        skills = [...skills.filter((k) => k.id !== r.skill.id), r.skill];
        toast(t("Skill ready: {name}", { name: r.skill.name }));
        renderSkills();
      });
      inp.click();
    };
    mount(skillsBox,
      h("ul", { class: "files" }, skills.length ? skills.map((k) => h("li", {}, "🧩 ", h("b", {}, k.name),
        h("small", { class: "muted" }, ` ${k.size ? Math.round(k.size / 1048576 * 10) / 10 + " MB · " : ""}${k.uploaded_at ? fmtTime(k.uploaded_at) : ""}`),
        h("button", { class: "link", onclick: () => pick(k.id) }, t("Update")),
        h("button", { class: "link", title: t("Remove"), onclick: async () => {
          if (!confirm(t("Stop using {name}? It is also deleted from your Claude account.", { name: k.name }))) return;
          const r = await callFn("ai-agent", { mode: "skill_remove", skill_id: k.id });
          if (!r.ok) return toast(r.error ?? "", true);
          skills = skills.filter((x) => x.id !== k.id);
          renderSkills();
        } }, "✕"))) : h("li", { class: "muted" }, t("No skills yet"))),
      h("button", { onclick: () => pick() }, "+ " + t("Add a skill (.zip)")));
  }
  renderSkills();

  // ---- log ----
  const logBox = h("div", {});
  async function renderLog() {
    const { data: lg } = await sb.from("ai_log").select("*").order("at", { ascending: false }).limit(50);
    mount(logBox, h("table", { class: "log" }, h("tbody", {}, (lg ?? []).map((r: any) => h("tr", {},
      h("td", {}, fmtTime(r.at)), h("td", {}, r.source), h("td", {}, h("b", {}, t(r.decision))), h("td", { dir: "ltr" }, r.wa_id ? "+" + r.wa_id : ""),
      h("td", {}, h("div", { dir: "auto" }, r.reply ?? ""), r.reason && h("small", { class: "muted" }, r.reason),
        h("small", { class: "muted used" }, usedText(r.used, r.tokens))))))),
      !(lg ?? []).length && h("p", { class: "muted" }, t("Nothing yet")));
  }

  mount(box,
    h("h2", {}, t("AI agent")),
    h("div", { class: "card-in" },
      h("h3", {}, t("How it works")),
      h("label", {}, t("Mode"), mode),
      h("p", { class: "muted" }, t("Auto replies only inside WhatsApp's 24-hour window, never to numbers that sent STOP, at most the hourly limit per chat. It hands the chat to a person when the customer asks for one, is upset, or the question is about the topics below.")),
      h("div", { class: "row" }, h("label", {}, t("Name"), name), h("label", {}, t("Replies in"), lang)),
      h("label", {}, t("Model"), model),
      h("div", { class: "row" }, h("label", {}, t("Auto replies per chat per hour"), perHour), h("label", {}, t("Hand over to"), handTo)),
      h("label", {}, t("Hand over on these topics"), topics),
      h("label", {}, t("Message to the customer on hand-over (auto mode; empty = none)"), handMsg),
      h("div", { class: "row end" }, h("button", { class: "primary", onclick: save }, t("Save")))),
    h("div", { class: "card-in" },
      h("h3", {}, t("Claude API key")),
      h("p", { class: "muted" }, t("The company's own key from console.anthropic.com (API keys). Usage is billed to that account. Stored in this project's Vault, never shown again.")),
      h("div", { class: "row" }, key, h("button", { onclick: saveKey }, keySet ? t("Save / check") : t("Save and check"))), keyOut),
    h("div", { class: "card-in" },
      h("h3", {}, t("What the AI knows")),
      h("p", { class: "muted" }, t("Write in Arabic, English or both. The AI answers only from this, the availability and the files.")),
      TEXTS.map(([k, label, hint]) => h("label", {}, t(label), h("small", { class: "hint" }, t(hint)), texts.get(k)!)),
      h("h4", {}, t("FAQs")), faqBox,
      h("div", { class: "row end" }, h("button", { class: "primary", onclick: save }, t("Save")))),
    h("div", { class: "card-in" },
      h("h3", {}, t("Availability")),
      h("p", { class: "muted" }, t("Units, products or slots with their status and price. Read fresh for every answer; with many rows only the ones matching the conversation are sent.")),
      table,
      h("label", {}, t("Or a CSV / Google Sheet link (first row = column names; Google Sheet: share as 'Anyone with the link')"), sheet),
      h("div", { class: "row end" }, h("button", { onclick: async () => {
        sheetOut.textContent = t("Reading…");
        const r = await callFn("ai-agent", { mode: "sheet", url: sheet.value.trim() });
        sheetOut.textContent = r.ok ? t("{n} rows. First: {first}", { n: r.rows, first: (r.first ?? []).slice(0, 2).join(" / ") }) : "✗ " + (r.error ?? "");
      } }, t("Test the link")), h("button", { class: "primary", onclick: save }, t("Save"))), sheetOut),
    h("div", { class: "card-in" }, h("h3", {}, t("Skills")),
      h("p", { class: "muted" }, t("A Claude skill (a zipped folder with SKILL.md, reference files and data files, up to 30 MB). It is stored in your Claude account; for each answer Claude opens only the files it needs and searches large lists. Answers take longer (often 10–40 seconds) and cost a little more.")),
      skillsBox),
    h("div", { class: "card-in" }, h("h3", {}, t("Files the AI reads")), filesBox),
    h("div", { class: "card-in" }, h("h3", {}, t("Try the AI")), h("p", { class: "muted" }, t("Save first: it answers with what is saved. Nothing is sent on WhatsApp.")), tryBox()),
    h("div", { class: "card-in" }, h("h3", {}, t("Log")), h("button", { onclick: renderLog }, t("Refresh")), logBox));
  renderLog();
}

const TEXT_EXT = /\.(md|markdown|txt|csv|json|ya?ml|html?|xml|py|js|ts|sql|ini|toml)$/i;
/** a zip (e.g. a Claude skill: SKILL.md + references): its text files joined into one "<zip name>.md" (each under a
 *  "# file: path" heading, SKILL.md first), its PDFs as separate files; images and other files are left out */
async function fromZip(f: File): Promise<{ name: string; blob: Blob }[]> {
  const entries = unzipSync(new Uint8Array(await f.arrayBuffer()));
  const base = f.name.replace(/\.zip$/i, "");
  const names = Object.keys(entries).filter((n) => !n.endsWith("/") && !/(^|\/)(__MACOSX|\.)/.test(n))
    .sort((a, b) => Number(/SKILL\.md$/i.test(b)) - Number(/SKILL\.md$/i.test(a)) || a.localeCompare(b));
  const text = names.filter((n) => TEXT_EXT.test(n)).map((n) => `# file: ${n}\n\n${strFromU8(entries[n]).trim()}\n`);
  const out: { name: string; blob: Blob }[] = [];
  if (text.length) out.push({ name: base + ".md", blob: new Blob([text.join("\n---\n\n")], { type: "text/markdown" }) });
  for (const n of names.filter((n) => /\.pdf$/i.test(n)))
    out.push({ name: base + " - " + n.split("/").pop(), blob: new Blob([entries[n] as BlobPart], { type: "application/pdf" }) });
  if (!out.length) throw new Error(t("No text or PDF files in this zip"));
  return out;
}

export function usedText(used: any, tokens: any): string {
  if (!used) return "";
  const parts = [];
  if (used.faqs?.length) parts.push("FAQ " + used.faqs.join(", "));
  if (used.rows?.length) parts.push(t("rows") + " " + used.rows.join(", "));
  if (used.files?.length) parts.push(t("files") + " " + used.files.join(", "));
  if (used.rows_sent != null) parts.push(`${used.rows_sent}/${used.rows_total} ${t("rows sent")}`);
  if (used.messages) parts.push(`${used.messages} ${t("messages")}`);
  if (used.skills?.length) parts.push("🧩 " + used.skills.join(", ") + (used.steps?.length ? ` (${used.steps.length} ${t("steps")}: ${used.steps.slice(0, 4).join(" · ")})` : ""));
  if (used.warnings?.length) parts.push("⚠ " + used.warnings.join("; "));
  if (tokens?.input != null) parts.push(`${tokens.input}+${tokens.cache_read ?? 0}c → ${tokens.output} tok`);
  return parts.join(" · ");
}

/** a chat with the AI as if you were a customer (no WhatsApp) */
function tryBox() {
  const turns: { from: "customer" | "us"; text: string; note?: string }[] = [];
  const msgs = h("div", { class: "msgs try" });
  const input = h("textarea", { rows: 2, dir: "auto", placeholder: t("Write as a customer, e.g. Is flat 101 free? / هل الشقة ١٠١ متاحة؟") });
  const name = h("input", { placeholder: t("Customer name (optional)") });
  const go = h("button", { class: "primary" }, t("Send"));
  const render = () => {
    mount(msgs, turns.map((x) => h("div", { class: "msg " + (x.from === "customer" ? "in" : "out ai") }, h("p", { dir: "auto" }, x.text),
      x.note && h("small", { class: "muted" }, x.note))));
    msgs.scrollTop = msgs.scrollHeight;
  };
  go.addEventListener("click", async () => {
    if (!input.value.trim()) return;
    turns.push({ from: "customer", text: input.value.trim() });
    input.value = "";
    render();
    go.disabled = true; go.textContent = "…";
    const r = await callFn("ai-agent", { mode: "try", turns, name: name.value });
    go.disabled = false; go.textContent = t("Send");
    if (!r.ok) return toast(r.error ?? "error", true);
    turns.push({ from: "us", text: r.reply || "—", note: [r.handover ? "🙋 " + t("would hand over: {why}", { why: r.handover_reason }) : "", usedText(r.used, r.tokens)].filter(Boolean).join(" · ") });
    render();
  });
  return h("div", { class: "try-box" }, msgs, h("div", { class: "row" }, name), h("div", { class: "row" }, input, go),
    h("button", { class: "link", onclick: () => { turns.length = 0; render(); } }, t("Start again")));
}
